import { type ReactNode } from 'react';
import { type Table as TanStackTable, type Row, flexRender } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { EmptyState } from '../EmptyState';

interface DataTableContentProps<TData> {
  table: TanStackTable<TData>;
  /** Shown when a filter or search empties an otherwise non-empty table. */
  filteredEmptyTitle?: string;
  filteredEmptyDescription?: string;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  renderExpandedRow?: (row: TData) => ReactNode;
  stickyHeader?: boolean;
  maxHeight?: string;
  caption?: string;
}

export function DataTableContent<TData>({
  table,
  filteredEmptyTitle = 'Nothing matches those filters',
  filteredEmptyDescription = 'Try widening the search, or clearing a filter.',
  onRowClick,
  rowClassName,
  renderExpandedRow,
  stickyHeader = false,
  maxHeight,
  caption,
}: DataTableContentProps<TData>) {
  const containerStyle =
    stickyHeader && maxHeight ? { maxHeight, overflow: 'auto' as const } : undefined;

  return (
    <div style={containerStyle}>
      <Table>
        {caption && <caption className="sr-only">{caption}</caption>}
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-muted')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canResize = header.column.getCanResize();
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    scope="col"
                    className={
                      (header.column.columnDef.meta as Record<string, unknown>)?.className as string
                    }
                    // Column headers use the eyebrow treatment from
                    // .text-table-header (11px / 600 / uppercase / wide tracking).
                    // Injecting a typography style here would override it, since
                    // inline styles beat classes.
                    style={{
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {canResize && (
                      <div
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                          'hover:bg-primary-500 active:bg-primary-500',
                          header.column.getIsResizing() && 'bg-primary-500'
                        )}
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table
              .getRowModel()
              .rows.map((row) => (
                <TableRowWithExpansion
                  key={row.id}
                  row={row}
                  onRowClick={onRowClick}
                  renderExpandedRow={renderExpandedRow}
                  rowClassName={rowClassName}
                  columnCount={table.getVisibleFlatColumns().length}
                />
              ))
          ) : (
            <TableRow>
              {/* A filter emptying the table used to print a bare "No results."
                  row, so every filterable list had two unrelated empty designs:
                  this one, and the EmptyState the table shows when it has no
                  data at all. Same component now, different words — because
                  "no records yet" and "nothing matches your filter" are
                  genuinely different situations and want different advice. */}
              <TableCell colSpan={table.getVisibleFlatColumns().length} className="p-0">
                <EmptyState
                  icon="search"
                  title={filteredEmptyTitle}
                  description={filteredEmptyDescription}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// Separated component for row rendering with expansion support
function TableRowWithExpansion<TData>({
  row,
  onRowClick,
  renderExpandedRow,
  rowClassName,
  columnCount,
}: {
  row: Row<TData>;
  onRowClick?: (row: TData) => void;
  renderExpandedRow?: (row: TData) => ReactNode;
  rowClassName?: (row: TData) => string | undefined;
  columnCount: number;
}) {
  return (
    <>
      <TableRow
        data-state={row.getIsSelected() && 'selected'}
        className={cn(
          // `border-subtle`, not `border-border`. This class is merged over the
          // one the primitive sets, so pointing the primitive at the lighter
          // weight did nothing until this followed it.
          'border-b border-subtle h-11',
          // A tint of the reader's own accent rather than grey. Grey reads as a
          // disabled state; a hue that belongs to the product reads as the row
          // answering the pointer.
          onRowClick && 'cursor-pointer hover:bg-row-hover',
          row.getIsSelected() && 'bg-muted',
          // Last, so a caller's emphasis wins over the base row background.
          rowClassName?.(row.original)
        )}
        onClick={() => onRowClick?.(row.original)}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={(cell.column.columnDef.meta as Record<string, unknown>)?.className as string}
            style={{ width: cell.column.getSize() }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      {row.getIsExpanded() && renderExpandedRow && (
        <TableRow>
          <TableCell colSpan={columnCount} className="bg-muted p-4">
            {renderExpandedRow(row.original)}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Helper: Selection column definition factory

// Helper: Expand column definition factory
